import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { TextField, InputAdornment, IconButton, TextFieldProps } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SearchBoxProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  onSearch?: (query: string) => void;
  onClear: () => void;
  backgroundColor?: string;
  fullWidth?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  borderRadius?: string | number;
  height?: string | number;
  borderColor?: string;
  defaultValue?: string;
}

export interface SearchBoxHandle {
  handleClear: () => void;
  input: HTMLInputElement | null;
}

const SearchBox = forwardRef<SearchBoxHandle, SearchBoxProps>(
  (
    {
      onSearch,
      onClear,
      onChange,
      backgroundColor = '#F2F7FB',
      fullWidth,
      sx,
      borderRadius,
      height,
      borderColor,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState(defaultValue || '');
    const internalInputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      handleClear,
      input: internalInputRef.current,
    }));
    
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      onChange?.(event);
    };

    function handleClear() {
      setInputValue('');
      onClear();
      if(internalInputRef.current) internalInputRef.current.value = '';
    }

    const handleSearch = () => {
      if (onSearch) {
        onSearch(inputValue);
      }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    };

    return (
      <TextField
        variant="outlined"
        // value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        fullWidth={fullWidth}
        {...props}
        sx={{
          ...sx,
          '.MuiInputBase-root': {
            background: backgroundColor || 'white',
            borderRadius: borderRadius,
            height: height,
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: borderColor,
          },
        }}
        InputProps={{
          inputRef: internalInputRef,
          endAdornment: (
            <InputAdornment position="end">
              {inputValue.length > 0 && (
                <IconButton onClick={handleClear} edge="end" aria-label={t('coreComponents.searchBox.clear', 'clear search')}>
                  <ClearIcon />
                </IconButton>
              )}
              <IconButton onClick={handleSearch} edge="end" aria-label={t('coreComponents.searchBox.search', 'search')}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }
);

export default SearchBox;